import { AuthProvider, oAuthStatus } from "../values";

const googleAuth = {
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    discoveryDocs: [
        'https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest',
        'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
        'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest',
    ],
    scope: 'https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/calendar.events',
};

export const initGoogleClient = () => {
    return new Promise((resolve, reject) => {
        if (!window.gapi) {
            reject(new Error("Google API client not loaded"));
            return;
        }

        const initClient = async () => {
            try {
                await window.gapi.client.init(googleAuth);
                const apiLoadResults = await Promise.allSettled([
                    window.gapi.client.load('calendar', 'v3'),
                    window.gapi.client.load('gmail', 'v1'),
                    window.gapi.client.load('drive', 'v3')
                ]);
                const gmailApiLoaded = apiLoadResults[1].status === 'fulfilled';
                if (!gmailApiLoaded) {
                    reject(new Error("Gmail API client not initialized properly"));
                    return;
                }
                if (window.gapi.auth2) {
                    const authInstance = window.gapi.auth2.getAuthInstance();
                    if (authInstance) {
                        authInstance.isSignedIn.listen((isSignedIn) => {
                            window.dispatchEvent(new CustomEvent('googleAuthStateChanged', {
                                detail: { isSignedIn }
                            }));
                        });
                    }
                }
                resolve();
            } catch (error) {
                reject(new Error(`Error initializing Google API client: ${error.message}`));
            }
        };
        window.gapi.load('client:auth2', initClient);
    });
};

export const handleGoogleLogin = async (authManager, setUser, setLoading, setError, setIsGoogleLoggedIn) => {
    setLoading(true);
    let authInstance = null;
    try {
        if (authManager && typeof authManager.canAddMoreAccounts === 'function') {
            const googleAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
            if (googleAccounts.length === 0 && !authManager.canAddMoreAccounts()) {
                const subscriptionInfo = authManager.getSubscriptionInfo();
                const tierName = subscriptionInfo?.name || 'Free';
                throw new Error(`Account limit reached for your ${tierName} plan. Please upgrade to add more accounts.`);
            }
        }
        await initGoogleClient();
        if (!window.gapi || !window.gapi.auth2) {
            throw new Error("Google Auth2 not initialized properly");
        }

        authInstance = window.gapi.auth2.getAuthInstance();
        if (!authInstance) {
            throw new Error("Failed to get Google Auth instance");
        }
        const isSignedIn = authInstance.isSignedIn.get();
        if (isSignedIn) {
            try {
                await authInstance.signOut();
            } catch (signOutError) { }
        }
        const signInOptions = {
            prompt: 'select_account',
            ux_mode: 'popup',
            scope: googleAuth.scope,
        };
        const googleUserResponse = await authInstance.signIn(signInOptions);
        if (!googleUserResponse) {
            throw new Error("Google sign-in returned no user data");
        }

        const googleUser = authInstance.currentUser.get();
        if (!googleUser || !googleUser.isSignedIn()) {
            throw new Error("Google sign-in failed or was cancelled");
        }
        const profile = googleUser.getBasicProfile();
        if (!profile) {
            throw new Error("Failed to get Google user profile");
        }
        const authResponse = googleUser.getAuthResponse(true);
        const user = {
            provider: AuthProvider.GOOGLE,
            id: profile.getId(),
            name: profile.getName(),
            email: profile.getEmail(),
            username: profile.getEmail(),
            photoURL: profile.getImageUrl(),
            accessToken: authResponse.access_token,
            tokenExpiry: authResponse.expires_at,
        };
        let account;
        if (authManager) {
            const existingAccount = authManager.getAccounts(AuthProvider.GOOGLE).find(acc =>
                acc.userData && (acc.userData.email === user.email || acc.userData.id === user.id)
            );
            if (existingAccount) {
                account = existingAccount;
                authManager.updateUserData(account.id, user);
                if (user.accessToken) {
                    const expiresIn = Math.max(0, (user.tokenExpiry - Date.now()) / 1000);
                    authManager.setAccessToken(account.id, user.accessToken, expiresIn);
                }
            } else {
                try {
                    account = authManager.addAccount(AuthProvider.GOOGLE, user);
                } catch (error) {
                    if (error.message && error.message.includes("Account limit reached")) {
                        throw error;
                    }
                    throw error;
                }
            }
            setUser(user);
            setIsGoogleLoggedIn(oAuthStatus.LOGGED_IN);
            authManager.notifyStateChange();
        } else {
            setUser(user);
            setIsGoogleLoggedIn(oAuthStatus.LOGGED_IN);
            localStorage.setItem('GoogleUserData', JSON.stringify(user));
            localStorage.setItem('GoogleAuth', JSON.stringify(oAuthStatus.LOGGED_IN));
        }
        return account || user;
    } catch (error) {
        if (authInstance && authInstance.isSignedIn && authInstance.isSignedIn.get()) {
            try {
                await authInstance.signOut();
            } catch (signOutError) { }
        }
        if (!error.message || !error.message.includes("Account limit reached")) {
            setError(`Google login failed: ${error.message}`);
        } else {
            setError(error.message);
        }
        return null;
    } finally {
        setLoading(false);
    }
};

export const handleGoogleLogout = async (authManager, accountId, setUser, setLoading, setError, setIsGoogleLoggedIn) => {
    try {
        setLoading(true);
        if (!authManager) {
            throw new Error("Auth manager is required");
        }
        const googleAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
        const account = googleAccounts.find(acc => acc.id === accountId);
        if (!account) {
            throw new Error("Invalid Google account");
        }
        let logoutSuccessful = true;
        try {
            if (window.gapi && window.gapi.auth2) {
                const authInstance = window.gapi.auth2.getAuthInstance();
                if (authInstance) {
                    const currentUser = authInstance.currentUser.get();
                    if (currentUser && currentUser.isSignedIn()) {
                        const profile = currentUser.getBasicProfile();
                        if (profile) {
                            const currentEmail = profile.getEmail();
                            const accountEmail = account.userData?.email || account.email;
                            if (currentEmail === accountEmail) {
                                try {
                                    const token = authManager.getAccessToken(accountId) ||
                                        (account.userData && account.userData.accessToken);
                                    if (token) {
                                        const revokeUrl = `https://accounts.google.com/o/oauth2/revoke?token=${token}`;
                                        await fetch(revokeUrl, { method: 'GET' });
                                    }
                                } catch (revokeError) { }
                                await authInstance.signOut();
                                try {
                                    await authInstance.disconnect();
                                } catch (disconnectError) { }
                            }
                        }
                    }
                }
            }
        } catch (googleError) {
            logoutSuccessful = false;
        }
        authManager.removeAccount(accountId);
        const remainingAccounts = authManager.getAccounts(AuthProvider.GOOGLE);
        if (remainingAccounts.length === 0) {
            setIsGoogleLoggedIn(oAuthStatus.NOT_LOGGED_IN);
            setUser(null);
        } else {
            setUser(remainingAccounts[0].userData);
        }
        try {
            localStorage.removeItem('GoogleUserData');
            localStorage.removeItem('GoogleAuth');
        } catch (storageError) { }
        authManager.notifyStateChange();
        return logoutSuccessful;
    } catch (error) {
        setError(`Google logout failed: ${error.message}`);
        return false;
    } finally {
        setLoading(false);
    }
};